import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, AlertTriangle, Camera, RectangleHorizontal } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import MiniSelect from "@/components/storyboard/MiniSelect";
import { STORYBOARD_STAGES, ASPECT_RATIOS, SHOT_TYPES } from "@/constants/shotSettings";
import { IconSketchBadge, IconConceptBadge, IconPrevisBadge } from "@/components/Icons";

const STAGE_ICON_MAP = { sketch: IconSketchBadge, concept: IconConceptBadge, previs: IconPrevisBadge };

export default function AddShotDialog({ insertAfter, onSubmit, onClose }) {
  const [prompt, setPrompt] = useState("");
  const [shotType, setShotType] = useState("medium");
  const [stage, setStage] = useState("sketch");
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [submitting, setSubmitting] = useState(false);

  const previsWarning = STORYBOARD_STAGES.find((s) => s.value === "previs")?.warning;
  const canSubmit = prompt.trim() && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit({
        prompt: prompt.trim(),
        shot_type: shotType,
        stage,
        aspect_ratio: aspectRatio,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="cine-edge bg-[var(--bg-surface)] border border-[var(--border-strong)] text-[var(--text-primary)] max-w-2xl p-0 gap-0 overflow-hidden shadow-[var(--shadow-2xl)]">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-[var(--border-subtle)] bg-gradient-to-b from-[var(--bg-elevated)] to-transparent">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--color-gold-500)] mb-1.5">
            {insertAfter ? `Insert · Scene ${insertAfter.scene_number}` : "New Shot"}
          </div>
          <DialogTitle className="text-2xl font-semibold tracking-tight text-[var(--text-primary)]">
            {insertAfter ? `After Shot ${insertAfter.frame_number}` : "Create a Shot"}
          </DialogTitle>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6 max-h-[68vh] overflow-y-auto">
          {/* Visual stage */}
          <section>
            <label className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] mb-3 block">
              Visual Stage
            </label>
            <div className="grid grid-cols-3 gap-3">
              {STORYBOARD_STAGES.map((s) => {
                const I = STAGE_ICON_MAP[s.value];
                const active = stage === s.value;
                return (
                  <motion.button
                    key={s.value}
                    type="button"
                    onClick={() => setStage(s.value)}
                    whileHover={{ y: -3 }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    className={`relative text-left rounded-xl border p-3.5 overflow-hidden transition-colors ${
                      active
                        ? "cine-selected"
                        : "border-[var(--border-default)] bg-[var(--bg-elevated)] hover:border-[var(--border-strong)]"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2.5">
                      <span
                        className={`grid place-items-center w-9 h-9 rounded-lg transition-colors ${
                          active ? "bg-[rgba(212,168,83,0.15)]" : "bg-[var(--bg-overlay)]"
                        }`}
                      >
                        {I && (
                          <I
                            size={20}
                            className={active ? "text-[var(--color-gold-300)]" : "text-[var(--text-secondary)]"}
                          />
                        )}
                      </span>
                      {s.value === "previs" && (
                        <AlertTriangle size={13} className="text-[var(--color-gold-500)]" />
                      )}
                    </div>
                    <div
                      className={`text-sm font-semibold leading-tight ${
                        active ? "text-[var(--color-gold-200)]" : "text-[var(--text-primary)]"
                      }`}
                    >
                      {s.label}
                    </div>
                    <p className="text-[11px] text-[var(--text-muted)] mt-1 leading-snug">{s.desc}</p>
                    {active && (
                      <motion.span
                        layoutId="add-shot-stage-glow"
                        className="absolute inset-0 rounded-xl pointer-events-none"
                        style={{ boxShadow: "var(--glow-gold)" }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>
            <AnimatePresence initial={false}>
              {stage === "previs" && previsWarning && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-3 flex gap-2.5 p-3 rounded-lg bg-[rgba(212,168,83,0.08)] border border-[rgba(212,168,83,0.2)]">
                    <AlertTriangle size={14} className="text-[var(--color-gold-500)] flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{previsWarning}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </section>

          {/* Shot type + aspect ratio */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] mb-2 block">
                Shot Type
              </label>
              <MiniSelect options={SHOT_TYPES} value={shotType} onChange={setShotType} icon={Camera} label="Shot type" />
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] mb-2 block">
                Aspect Ratio
              </label>
              <MiniSelect
                options={ASPECT_RATIOS}
                value={aspectRatio}
                onChange={setAspectRatio}
                icon={RectangleHorizontal}
                label="Aspect ratio"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] mb-2 block">
              Shot Description
            </label>
            <Textarea
              data-testid="shot-prompt-textarea"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="What happens in this shot? e.g. 'John edges through the doorway, scanning the dark room.'"
              className="bg-[var(--bg-elevated)] border-[var(--border-default)] text-[var(--text-primary)] placeholder:text-[var(--text-disabled)] focus:border-[var(--color-gold-500)] focus-visible:ring-0 resize-none font-mono text-sm min-h-[110px] rounded-lg"
            />
            <p className="text-[11px] text-[var(--text-muted)] mt-2">
              Be specific about action, emotion, camera position, and key visual details.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--border-subtle)] flex gap-3 bg-black/20">
          <button
            type="button"
            data-testid="add-shot-submit-btn"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-lg font-semibold text-[var(--text-inverse)] transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed ${
              canSubmit ? "cine-shimmer shadow-[var(--shadow-glow-gold)]" : ""
            }`}
            style={{ background: "var(--gold-gradient)" }}
          >
            <Sparkles size={17} />
            {submitting ? "Generating…" : "Generate Shot"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="h-11 px-5 rounded-lg border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors"
          >
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
